import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '@/i18n';
import { useToast } from '@/components/ui/Toast';
import { dataService } from '@/services/dataService';
import { DemoSettings } from '@/services/demoDataService';
import { ConfirmModal } from '@/components/ui/ConfirmModal';
import { AI_PROVIDERS } from '@/config/aiProviders.config';
import { ENV } from '@/config/env';
import { DATA_MODE } from '@/config/dataMode';

/**
 * Everything on this page that CAN work locally, does — every field below
 * reads/writes real state through dataService/demoDataService and
 * persists across a refresh (see DemoSettings in demoDataService.ts).
 * A few fields (Creators/Partnerships/Contracts ID prefixes, the Contract
 * expiry-warning threshold used by Overview's "Expiring Contracts" card,
 * Reduced Motion) already drive real downstream behavior. Others persist
 * correctly but don't have a consuming feature yet (Agent defaults,
 * Follow-up/Approval defaults) — those are labeled honestly rather than
 * silently claiming an effect they don't have yet, and Security/AI-key
 * entry/Support & Diagnostics stay explicitly gated because those
 * genuinely need a backend to be real rather than theater.
 */
export function SettingsPage() {
  const { t, setLang, lang } = useI18n();
  const push = useToast();
  const navigate = useNavigate();
  const [settings, setSettings] = useState<DemoSettings | null>(null);
  const [confirmReset, setConfirmReset] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const [connectorSummary, setConnectorSummary] = useState({ total: 0, connected: 0 });

  function reload() {
    dataService.getSettings().then(setSettings);
  }
  useEffect(reload, []);
  useEffect(() => {
    dataService.listConnectorStatuses().then((list) =>
      setConnectorSummary({ total: list.length, connected: list.filter((c) => c.status === 'connected').length })
    );
  }, []);

  async function save(patch: Partial<DemoSettings>) {
    await dataService.updateSettings(patch);
    push(t('toast.settingsSaved'), 'success');
    reload();
  }

  if (!settings) return <div className="card" style={{ padding: 20 }}>Loading…</div>;

  return (
    <div>
      <h1>{t('nav.settings')}</h1>

      <SectionCard title={t('settingsSections.general')}>
        <Row label={t('settingsFields.companyName')}>
          <input className="text-input" defaultValue={settings.companyName} onBlur={(e) => save({ companyName: e.target.value })} />
        </Row>
        <Row label={t('settingsFields.legalName')}>
          <input className="text-input" defaultValue={settings.legalName} onBlur={(e) => save({ legalName: e.target.value })} />
        </Row>
        <Row label={t('settingsFields.timezone')}>
          <input className="text-input" defaultValue={settings.timezone} onBlur={(e) => save({ timezone: e.target.value })} />
        </Row>
        <Row label={t('settingsFields.defaultCurrency')}>
          <input className="text-input" defaultValue={settings.defaultCurrency} onBlur={(e) => save({ defaultCurrency: e.target.value })} />
        </Row>
        <Row label={t('settingsFields.environment')}><span>{t(`env.${ENV.environment}`)}</span></Row>
      </SectionCard>

      <SectionCard title={t('settingsSections.branding')}>
        <Row label={t('settingsFields.appName')}>
          <input className="text-input" defaultValue={settings.branding.appName} onBlur={(e) => save({ branding: { ...settings.branding, appName: e.target.value } })} />
        </Row>
        <Row label={t('settingsFields.brandColor')}>
          <input type="color" value={settings.branding.brandColor} onChange={(e) => save({ branding: { ...settings.branding, brandColor: e.target.value } })} style={{ width: 44, height: 28, padding: 0, border: 'none', background: 'none', cursor: 'pointer' }} />
        </Row>
        <Row label={t('settingsFields.logoStatus')}><span className="soon-tag-inline">{t('settingsFields.noLogo')}</span></Row>
        <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{t('settingsFields.logoNote')}</div>
      </SectionCard>

      <SectionCard title={t('settingsSections.appearance')}>
        <Row label={t('settingsFields.theme')}>
          <select className="text-input" value={settings.theme} onChange={(e) => save({ theme: e.target.value as DemoSettings['theme'] })}>
            <option value="light">Light</option>
            <option value="dark">Dark</option>
          </select>
        </Row>
        <Row label={t('settingsFields.density')}>
          <select className="text-input" value={settings.density} onChange={(e) => save({ density: e.target.value as DemoSettings['density'] })}>
            <option value="comfortable">Comfortable</option>
            <option value="compact">Compact</option>
          </select>
        </Row>
        <Row label={t('settingsFields.sidebarCollapsed')}>
          <input type="checkbox" checked={settings.sidebarCollapsed} onChange={(e) => save({ sidebarCollapsed: e.target.checked })} />
        </Row>
        <Row label={t('settingsFields.reducedMotion')}>
          <input
            type="checkbox"
            checked={settings.reducedMotion}
            onChange={(e) => {
              document.documentElement.setAttribute('data-reduced-motion', String(e.target.checked));
              save({ reducedMotion: e.target.checked });
            }}
          />
        </Row>
      </SectionCard>

      <SectionCard title={t('settingsSections.language')}>
        <Row label={t('settingsFields.appLanguage')}>
          <select className="text-input" value={lang} onChange={(e) => setLang(e.target.value as 'ar' | 'en')}>
            <option value="ar">العربية</option>
            <option value="en">English</option>
          </select>
        </Row>
        <Row label={t('settingsFields.dateFormat')}>
          <select className="text-input" value={settings.dateFormat} onChange={(e) => save({ dateFormat: e.target.value as DemoSettings['dateFormat'] })}>
            <option value="YYYY-MM-DD">YYYY-MM-DD</option>
            <option value="DD/MM/YYYY">DD/MM/YYYY</option>
            <option value="MM/DD/YYYY">MM/DD/YYYY</option>
          </select>
        </Row>
        <Row label={t('settingsFields.numberFormat')}>
          <select className="text-input" value={settings.numberFormat} onChange={(e) => save({ numberFormat: e.target.value as DemoSettings['numberFormat'] })}>
            <option value="western">1,234.56</option>
            <option value="arabic-indic">١٬٢٣٤٫٥٦</option>
          </select>
        </Row>
      </SectionCard>

      <SectionCard title={t('settingsSections.notifications')}>
        {(['followupDue', 'approvalWaiting', 'creatorAtRisk', 'contractExpiring', 'systemAlerts'] as const).map((key) => (
          <Row key={key} label={t(`settingsFields.notif.${key}`)}>
            <input type="checkbox" checked={settings.notificationPrefs[key]} onChange={(e) => save({ notificationPrefs: { ...settings.notificationPrefs, [key]: e.target.checked } })} />
          </Row>
        ))}
        <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{t('settingsFields.notif.disclaimer')}</div>
      </SectionCard>

      <SectionCard title={t('settingsSections.creatorRules')}>
        <Row label={t('settingsFields.defaultFollowupDays')}>
          <input type="number" className="text-input" defaultValue={settings.creatorRules.defaultFollowupDays} onBlur={(e) => save({ creatorRules: { ...settings.creatorRules, defaultFollowupDays: Number(e.target.value) } })} />
        </Row>
        <Row label={t('settingsFields.riskThresholdDays')}>
          <input type="number" className="text-input" defaultValue={settings.creatorRules.riskThresholdDays} onBlur={(e) => save({ creatorRules: { ...settings.creatorRules, riskThresholdDays: Number(e.target.value) } })} />
        </Row>
        <Row label={t('settingsFields.defaultOwner')}>
          <input className="text-input" defaultValue={settings.creatorRules.defaultOwner} onBlur={(e) => save({ creatorRules: { ...settings.creatorRules, defaultOwner: e.target.value } })} />
        </Row>
        <Row label={t('settingsFields.idPrefix')}>
          <input className="text-input" defaultValue={settings.creatorRules.idPrefix} onBlur={(e) => save({ creatorRules: { ...settings.creatorRules, idPrefix: e.target.value.toUpperCase() } })} style={{ width: 90 }} />
        </Row>
      </SectionCard>

      <SectionCard title={t('settingsSections.followupRules')}>
        <Row label={t('settingsFields.defaultPriority')}>
          <select className="text-input" value={settings.followupRules.defaultPriority} onChange={(e) => save({ followupRules: { ...settings.followupRules, defaultPriority: e.target.value as DemoSettings['followupRules']['defaultPriority'] } })}>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
          </select>
        </Row>
        <Row label={t('settingsFields.overdueBehavior')}>
          <select className="text-input" value={settings.followupRules.overdueBehavior} onChange={(e) => save({ followupRules: { ...settings.followupRules, overdueBehavior: e.target.value as DemoSettings['followupRules']['overdueBehavior'] } })}>
            <option value="flag">{t('settingsFields.overdueBehaviorFlag')}</option>
            <option value="escalate">{t('settingsFields.overdueBehaviorEscalate')}</option>
          </select>
        </Row>
        <Row label={t('settingsFields.reminderEnabled')}>
          <input type="checkbox" checked={settings.followupRules.reminderEnabled} onChange={(e) => save({ followupRules: { ...settings.followupRules, reminderEnabled: e.target.checked } })} />
        </Row>
      </SectionCard>

      <SectionCard title={t('settingsSections.approvalRules')}>
        <Row label={t('settingsFields.defaultPermissionLevel')}>
          <select className="text-input" value={settings.approvalRules.defaultPermissionLevel} onChange={(e) => save({ approvalRules: { ...settings.approvalRules, defaultPermissionLevel: Number(e.target.value) as 1 | 2 | 3 } })}>
            <option value={1}>1</option>
            <option value={2}>2</option>
            <option value={3}>3</option>
          </select>
        </Row>
        <Row label={t('settingsFields.expirationDays')}>
          <input type="number" className="text-input" defaultValue={settings.approvalRules.expirationDays} onBlur={(e) => save({ approvalRules: { ...settings.approvalRules, expirationDays: Number(e.target.value) } })} />
        </Row>
        <Row label={t('settingsFields.highRiskConfirm')}>
          <input type="checkbox" checked={settings.approvalRules.highRiskConfirm} onChange={(e) => save({ approvalRules: { ...settings.approvalRules, highRiskConfirm: e.target.checked } })} />
        </Row>
      </SectionCard>

      <SectionCard title={t('settingsSections.partnershipRules')}>
        <Row label={t('settingsFields.idPrefix')}>
          <input className="text-input" defaultValue={settings.partnershipRules.idPrefix} onBlur={(e) => save({ partnershipRules: { ...settings.partnershipRules, idPrefix: e.target.value.toUpperCase() } })} style={{ width: 110 }} />
        </Row>
        <Row label={t('settingsFields.defaultCurrency')}>
          <input className="text-input" defaultValue={settings.partnershipRules.defaultCurrency} onBlur={(e) => save({ partnershipRules: { ...settings.partnershipRules, defaultCurrency: e.target.value } })} />
        </Row>
        <Row label={t('settingsFields.renewalWarningDays')}>
          <input type="number" className="text-input" defaultValue={settings.partnershipRules.renewalWarningDays} onBlur={(e) => save({ partnershipRules: { ...settings.partnershipRules, renewalWarningDays: Number(e.target.value) } })} />
        </Row>
      </SectionCard>

      <SectionCard title={t('settingsSections.contractRules')}>
        <Row label={t('settingsFields.idPrefix')}>
          <input className="text-input" defaultValue={settings.contractRules.idPrefix} onBlur={(e) => save({ contractRules: { ...settings.contractRules, idPrefix: e.target.value.toUpperCase() } })} style={{ width: 110 }} />
        </Row>
        <Row label={t('settingsFields.expiryWarningDays')}>
          <input type="number" className="text-input" defaultValue={settings.contractRules.expiryWarningDays} onBlur={(e) => save({ contractRules: { ...settings.contractRules, expiryWarningDays: Number(e.target.value) } })} />
        </Row>
        <Row label={t('settingsFields.renewalWarningDays')}>
          <input type="number" className="text-input" defaultValue={settings.contractRules.renewalWarningDays} onBlur={(e) => save({ contractRules: { ...settings.contractRules, renewalWarningDays: Number(e.target.value) } })} />
        </Row>
        <Row label={t('settingsFields.defaultNoticePeriodDays')}>
          <input type="number" className="text-input" defaultValue={settings.contractRules.defaultNoticePeriodDays} onBlur={(e) => save({ contractRules: { ...settings.contractRules, defaultNoticePeriodDays: Number(e.target.value) } })} />
        </Row>
        <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{t('settingsFields.expiryWarningDays')} → {t('overview.expiringContracts')} ({t('nav.overview')}).</div>
      </SectionCard>

      <SectionCard title={t('settingsSections.agentRules')}>
        <Row label={t('settingsFields.defaultPermissionLevel')}>
          <select className="text-input" value={settings.agentRules.defaultPermissionLevel} onChange={(e) => save({ agentRules: { defaultPermissionLevel: Number(e.target.value) as 1 | 2 | 3 } })}>
            <option value={1}>1</option>
            <option value={2}>2</option>
            <option value={3}>3</option>
          </select>
        </Row>
        <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{t('settingsFields.agentDefaultsNote')}</div>
      </SectionCard>

      <SectionCard title={t('settingsSections.automationRules')}>
        <Row label={t('settingsFields.defaultApprovalLevel')}>
          <select className="text-input" value={settings.automationRules.defaultApprovalLevel} onChange={(e) => save({ automationRules: { defaultApprovalLevel: Number(e.target.value) as 1 | 2 | 3 } })}>
            <option value={1}>{t('permission.level1')}</option>
            <option value={2}>{t('permission.level2')}</option>
            <option value={3}>{t('permission.level3')}</option>
          </select>
        </Row>
        <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{t('settingsFields.automationDefaultsNote')}</div>
      </SectionCard>

      <SectionCard title={t('settingsSections.export')}>
        <Row label={t('settingsFields.defaultFileType')}>
          <select className="text-input" value={settings.exportSettings.defaultFileType} onChange={(e) => save({ exportSettings: { ...settings.exportSettings, defaultFileType: e.target.value as 'csv' | 'pdf' } })}>
            <option value="csv">CSV</option>
            <option value="pdf" disabled>{t('settingsFields.pdfUnavailable')}</option>
          </select>
        </Row>
        <Row label={t('settingsFields.includeBranding')}>
          <input type="checkbox" checked={settings.exportSettings.includeBranding} onChange={(e) => save({ exportSettings: { ...settings.exportSettings, includeBranding: e.target.checked } })} />
        </Row>
        <Row label={t('settingsFields.includeTimestamp')}>
          <input type="checkbox" checked={settings.exportSettings.includeTimestamp} onChange={(e) => save({ exportSettings: { ...settings.exportSettings, includeTimestamp: e.target.checked } })} />
        </Row>
        <Row label={t('settingsFields.fileNamePrefix')}>
          <input className="text-input" defaultValue={settings.exportSettings.fileNamePrefix} onBlur={(e) => save({ exportSettings: { ...settings.exportSettings, fileNamePrefix: e.target.value } })} />
        </Row>
      </SectionCard>

      <SectionCard title={t('settingsSections.aiProviders')}>
        {Object.values(AI_PROVIDERS).map((p) => (
          <Row key={p.key} label={p.displayName}>
            <span className="soon-tag-inline">{t('common.awaitingCredentials')}</span>
          </Row>
        ))}
        <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{t('settingsFields.aiKeyNote')}</div>
      </SectionCard>

      <SectionCard title={t('settingsSections.security')}>
        {['2FA', 'Session Timeout', 'RBAC', 'Audit Log'].map((s) => (
          <Row key={s} label={s}><span className="soon-tag-inline">{t('common.requiresBackend')}</span></Row>
        ))}
      </SectionCard>

      <SectionCard title={t('settingsSections.connectors')}>
        <Row label={t('settingsFields.connectorsSummary')}>
          <span>{connectorSummary.connected}/{connectorSummary.total}</span>
        </Row>
        <button onClick={() => navigate('/connectors')}>{t('settingsFields.viewConnectors')}</button>
      </SectionCard>

      <SectionCard title={t('settingsSections.developer')}>
        <Row label={t('settingsFields.environment')}><span>{t(`env.${ENV.environment}`)}</span></Row>
        <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{t('settingsFields.buildInfo')}</div>
      </SectionCard>

      <SectionCard title={t('settingsSections.support')}>
        <Row label={t('settingsFields.diagnosticsReminder')}>
          <input
            type="checkbox"
            checked={settings.supportRules.diagnosticsReminderEnabled}
            onChange={(e) => save({ supportRules: { diagnosticsReminderEnabled: e.target.checked } })}
          />
        </Row>
        <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 10 }}>{t('settingsFields.diagnosticsReminderNote')}</div>
        <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 10 }}>{t('settingsFields.agentLogRetentionNote')}</div>
        <button onClick={() => navigate('/support')}>{t('settingsFields.openSupport')}</button>
      </SectionCard>

      <SectionCard title={t('settingsSections.data')}>
        <Row label={t('settingsFields.dataMode')}><span>{DATA_MODE}</span></Row>
        {DATA_MODE === 'demo' && (
          <>
            <p style={{ fontSize: 13, color: 'var(--text-2)', display: DATA_MODE === 'demo' ? 'block' : 'none' }}>{t('settingsFields.demoDataExplanation')}</p>
            <div style={{ display: DATA_MODE === 'demo' ? 'flex' : 'none', gap: 8 }}>
              <button onClick={() => setConfirmReset(true)}>{t('settingsFields.resetDemoData')}</button>
              <button style={{ color: 'var(--crit)' }} onClick={() => setConfirmClear(true)}>{t('settingsFields.clearAllDemoData')}</button>
            </div>
          </>
        )}
      </SectionCard>

      {DATA_MODE === 'demo' && (
        <>
          <ConfirmModal
            open={DATA_MODE === 'demo' && confirmReset}
            title={t('confirm.resetDemoTitle')}
            description={t('confirm.resetDemoDesc')}
            reversible={false}
            confirmLabel={t('settingsFields.resetDemoData')}
            onConfirm={async () => { await dataService.resetDemoData(); push(t('toast.demoReset'), 'success'); setConfirmReset(false); window.location.reload(); }}
            onCancel={() => setConfirmReset(false)}
          />
          <ConfirmModal
            open={DATA_MODE === 'demo' && confirmClear}
            title={t('confirm.clearDemoTitle')}
            description={t('confirm.clearDemoDesc')}
            reversible={false}
            tone="destructive"
            confirmLabel={t('settingsFields.clearAllDemoData')}
            onConfirm={async () => { await dataService.clearAllDemoData(); push(t('toast.demoCleared'), 'warn'); setConfirmClear(false); window.location.reload(); }}
            onCancel={() => setConfirmClear(false)}
          />
        </>
      )}
    </div>
  );
}

function SectionCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="card" style={{ padding: 16, marginBottom: 14 }}>
      <div style={{ fontWeight: 700, marginBottom: 10 }}>{title}</div>
      {children}
    </div>
  );
}
function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="settings-row">
      <span>{label}</span>
      <span>{children}</span>
    </div>
  );
}
